import Head from "next/head";
import { useRouter } from "next/router";
import resources, { SITE_URL } from "@api/constants/resources";
import { generateBreadcrumbItems } from "@/utils/breadcrumb";
import { getPageType } from "@/utils/breadcrumb/pageTypes";
import { defaultSeoConfig } from "@/utils/seo/SeoDefault";

const Seo = ({ title, desc, image, keywords, breadcrumbData, id }) => {
  const router = useRouter();

  const pageTitle = title ? `${title} :: 칼렛스토어` : defaultSeoConfig.title;
  const finalDescription = desc ? desc : defaultSeoConfig.description;
  const finalImage = image ? image : defaultSeoConfig.image;
  const finalKeywords = keywords ? keywords : defaultSeoConfig.keywords;

  // URL 정보 추출 및 가공
  const pathSegments = router.asPath.split("/").filter(Boolean);
  // 페이지 타입 판별
  const pageType = getPageType(pathSegments);
  // 페이지네이션 확인
  const isProductPage = pathSegments[0] === "products";
  const lastSegment = pathSegments[pathSegments.length - 1];
  const isPaginatedProductPage =
    isProductPage && !isNaN(parseInt(lastSegment)) && isFinite(lastSegment);

  // Canonical URL 생성
  let canonicalUrl;
  if (isPaginatedProductPage) {
    const pathWithoutPagination = pathSegments.slice(0, -1).join("/");
    canonicalUrl = `${SITE_URL}/${pathWithoutPagination}`;
  } else {
    canonicalUrl = `${SITE_URL}${router.asPath}`;
  }

  // 상품 ID 패턴 확인 (P로 시작하는 ID)
  const hasPIdPattern = pathSegments.some((segment) =>
    /^P\d{10,}$/.test(segment)
  );

  //    Breadcrumb Schema 생성 함수
  const generateBreadcrumbSchema = () => {
    // 새로운 유틸리티 함수 활용하여 브레드크럼 아이템 생성
    const breadcrumbItems = generateBreadcrumbItems(
      pageType,
      breadcrumbData || {},
      pathSegments,
      canonicalUrl,
      id
    );

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems,
    };
  };
  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="author" content={defaultSeoConfig.author} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="image" content={finalImage} />

      <meta name="robots" content="index, follow" />

      {/* canonical URL - P로 시작하는 ID가 없을 때만 표시 */}
      {!hasPIdPattern && <link rel="canonical" href={canonicalUrl} />}

      {/* naver-site-verification START */}
      <meta
        name="naver-site-verification"
        content="73434c46afaf95b1aca89b69bf1bbd9b18d465c8"
      />
      {/* naver-site-verification END */}
      <meta httpEquiv="Cache-Control" content="no-cache" />
      <meta httpEquiv="Expires" content="0" />
      <meta httpEquiv="Pragma" content="no-cache" />

      {/*  Open Graph -->  */}
      <meta property="og:site_name" content={defaultSeoConfig.siteName} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={finalImage} />
      <meta name="og:keywords" content={finalKeywords} />

      {/*  Twitter Card -->  */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />

      <link rel="icon" href="/favicon.svg" />
      <link rel="manifest" href="/manifest.json" />
      <meta name="theme-color" content="#3bb77e" />
      {/* google-site-verification Start */}
      <meta
        name="google-site-verification"
        content="Rra2K_-5diyQmqHmSncqz4EJUQ3aXQLOgJ95bsVqrDY"
      />
      {/* google-site-verification END */}

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateBreadcrumbSchema()),
        }}
      />
    </Head>
  );
};

export default Seo;
